import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbCalendar, NgbDate, NgbDateAdapter, NgbDateParserFormatter, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { SharedConstants } from '@shared/constants';
import { FormatDatePipe } from '@shared/pipes/format-date.pipe';
import { SasDynamicFormService } from '@shared/sas-dynamic-form/sas-dynamic-form.service';
import { SasLabelValueComponent } from '@shared/sas-label-value/sas-label-value.component';
import { CustomDateAdapterService } from '@shared/services/custom-date-adapter.service';
import { CustomDateParserFormatterService } from '@shared/services/custom-date-parser-formatter.service';
import { FormSubmittedService } from '@shared/services/form-submitted.service';

@Component({
    selector: 'sas-datepicker-field-type',
    standalone: true,
    imports: [CommonModule, NgbDatepickerModule, TranslateModule, ReactiveFormsModule, FormlyModule, SasLabelValueComponent],
    providers: [
        { provide: NgbDateAdapter, useClass: CustomDateAdapterService },
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatterService },
        FormatDatePipe
    ],
    styleUrl: './sas-datepicker-field-type.component.scss',
    template: `
        @if (isShowingSummaryInfo) {
            <sas-label-value [label]="field.props.label ?? ''"
                             [model]="(formControl.value | date: format) ?? ''"
                             sasClass="textmode toplabel mb-2">
            </sas-label-value>
        } @else {
            @if (field.props.label?.length){
            <label [for]="instanceId"
                   [id]="field.id + 'label'"
                   title="{{field.props.label ?? '' | translate}}"
                   [textContent]="field.props.label"
                   translate="{{field.props.label}}"
                   class="form-label form-date-label">
            </label>
            @if (field.props.required) {
            <span> *</span>
            }
            <div [title]="helpText" class="sas-inline-block">
                @if (!isReadonly) {
                <i class="bi bi-info-circle-fill" style="font-size: 15px; cursor: help; margin-left: 5px;"></i>
                <span [id]="field.id + 'Help'" class="visually-hidden">{{helpText}}</span>
                }
            </div>
            }
            <div class="row mb-3">
                <div class="col-12">
                    <div class="input-group">
                        <input ngbDatepicker
                               [id]="instanceId"
                               [name]="field.name"
                               class="form-control"
                               #datepicker="ngbDatepicker"
                               [formControl]="formControl"
                               [placeholder]="isReadonly ? '' : (placeholder + ' ' + format)"
                               required="{{field.props.required}}"
                               [footerTemplate]="footerTemplate"
                               [ngClass]="{'is-invalid' : field.formControl.invalid && (field.formControl.dirty || field.formControl.touched || formSubmittedService.isSubmitted())}"
                               [readOnly]="isReadonly"
                               [autoClose]="'outside'"
                               outsideDays="hidden"
                               tabindex="0"
                               [attr.aria-invalid]="false"
                               [attr.aria-describedby]="field.id + 'Help'"/>
                        @if (!isReadonly){
                        <button class="btn btn-outline-secondary bi bi-calendar3" (click)="datepicker.toggle()" [disabled]="field.props.readonly" type="button">
                            <span class="visually-hidden">Toggle date picker</span>
                        </button>
                        }
                    </div>
                </div>
                <div [id]="field.id + 'Desc'" role="alert" aria-atomic="true" class="invalid-feedback">
                    <small>{{ sasDynamicFormService.getFieldErrorMessage(field) }}</small>
                </div>
            </div>
            <ng-template #footerTemplate>
                <hr class="my-0">
                <button class="btn btn-primary btn-sm m-2 float-start"
                        type="button"
                        (click)="onDateSelection(today); datepicker.close()">
                    Today
                </button>
                <button class="btn btn-secondary btn-sm m-2 float-end"
                        type="button"
                        (click)="datepicker.close()">
                    Close
                </button>
            </ng-template>
        }
    `
})
export class SasDatepickerFieldTypeComponent extends FieldType<FieldTypeConfig> {
    formSubmittedService = inject(FormSubmittedService);
    customFormatter = inject(CustomDateParserFormatterService);
    private translate = inject(TranslateService);
    sasDynamicFormService = inject(SasDynamicFormService);
    private ngbCalendar = inject(NgbCalendar);

    instanceId: string = '';
    private random = Math.random().toString(36).substring(2);
    labelId: string = '';
    helpText!: string;
    placeholder!: string;
    ariaRequired: boolean = false;
    format = SharedConstants.dateOutputFormat;
    today!: NgbDate;
    isShowingSummaryInfo: boolean = false;

    constructor() {
        super()
    }

    get isReadonly(): boolean {
        return (this.field.formControl.disabled || this.field.props.readonly) ?? false;
    }

    ngOnInit() {
        this.instanceId = this.field.id + this.random;

        if (this.field.props['helptext']) {
            const helpText = this.translate.instant(this.field.props['helptext']);

            this.helpText = helpText === this.field.props['helptext'] ? '' : helpText;
            this.helpText = this.helpText.length > 0 ? `${this.helpText}. Format is dd/mm/yyyy` : 'Format is dd/mm/yyyy';
        }
        else {
            this.helpText = 'Format is dd/mm/yyyy';
        }

        this.placeholder = this.field.props['placeholder'] ?? '';
        this.today = this.ngbCalendar.getToday();

        this.formControl.setValidators([
            ...(this.field.props.required ? [Validators.required] : []),
            this.dateValidator
        ]);
        const initialValue = this.customFormatter.formatToOutput(this.formControl.value);

        this.formControl.setValue(initialValue || null);
        this.isShowingSummaryInfo = this.field.props['isShowingSummaryInfo'] ?? false;
    }

    dateValidator(control: AbstractControl): { [key: string]: any } | null {
        const { value } = control;

        if (!value || typeof value !== 'string' || value.trim() === '') return null;
        function formatCorrectDate(dateString: string): string | null {
            const delimiter = dateString.includes('/') ? '/' : '-';
            const parts = dateString.split(delimiter);

            if (parts.length !== 3) {
                return null;
            }

            let day: string;
            let month: string;
            let year: string;

            if (delimiter === '/') {
                [day, month, year] = parts;
            }
            else {
                [year, month, day] = parts;
            }

            if (!day || !month || !year) {
                return null;
            }

            return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
        }

        const formattedDate = formatCorrectDate(value);

        const datePattern1 = /^\d{2}\/\d{2}\/\d{4}$/;
        const datePattern2 = /^\d{4}-\d{2}-\d{2}$/;

        if (!formattedDate || (!datePattern1.test(formattedDate) && !datePattern2.test(value))) {
            return { 'invalidDate': true };
        }

        try {
            const [day, month, year] = formattedDate.split('/');
            const date = new Date(`${year}-${month}-${day}`);
            return isNaN(date.getTime()) ? { 'invalidDate': true } : null;
        } catch {
            return { 'invalidDate': true };
        }
    }

    onDateSelection(date: NgbDate) {
        let formattedDate = this.customFormatter.formatToOutput(date);

        if (formattedDate.includes('NaN')) {
            formattedDate = '';
        }

        this.formControl.setValue(formattedDate || null);
    }
}
